import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'

import * as api from '../api/katalog.api.js'
import { ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthProvider.tsx'

function pesan(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Tidak dapat menghubungi server'
  const detail = error.details?.map((d) => `${d.field}: ${d.message}`).join('; ')
  return detail !== undefined && detail !== '' ? detail : error.message
}

const BREAKER_CLASS: Record<api.BreakerState, string> = {
  TERTUTUP: 'badge-diterbitkan',
  SETENGAH_TERBUKA: 'badge-diajukan',
  TERBUKA: 'badge-ditolak',
}

const JOB_CLASS: Record<api.JobStatus, string> = {
  SELESAI: 'badge-diterbitkan',
  BERJALAN: 'badge-diajukan',
  GAGAL: 'badge-ditolak',
  DIBATALKAN: '',
}

function waktu(iso: string | null): string {
  return iso === null ? '—' : iso.slice(0, 19).replace('T', ' ')
}

/**
 * Katalog station dan kesehatan konektor — W2-A06, dasar G2-10.
 *
 * Halaman ini ada karena katalog yang tidak dapat dilihat siapa pun tidak dapat
 * diperiksa benar-salahnya. Rubrik delapan kompetensi yang ditarik dari Bank
 * Soal adalah instrumen yang dipakai menilai peserta; kekeliruan di dalamnya
 * baru ketahuan di TUK bila tidak ada yang pernah membukanya lebih dulu.
 */
export function KatalogPage(): ReactNode {
  const { user } = useAuth()
  const bolehMenarik = user?.roles.some((r) => r.role === 'PANITIA_PUSAT') ?? false

  const [health, setHealth] = useState<api.ConnectorHealth | null>(null)
  const [stations, setStations] = useState<readonly api.StationSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [dibuka, setDibuka] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const muat = useCallback(async (halaman: number) => {
    try {
      const [h, s] = await Promise.all([api.health(), api.listStations(halaman)])
      setHealth(h)
      setStations(s.rows)
      setTotal(s.total)
      setError(null)
    } catch (err) {
      setError(pesan(err))
    }
  }, [])

  useEffect(() => {
    void muat(page)
  }, [muat, page])

  async function tarik(): Promise<void> {
    setBusy(true)
    setError(null)
    try {
      const { id } = await api.startPull()
      await pantau(id)
    } catch (err) {
      setError(pesan(err))
    } finally {
      setBusy(false)
    }
  }

  /**
   * Menunggu penarikan selesai dengan menanyakan kemajuannya berkala.
   *
   * Penarikan berjalan di latar di sisi server; halaman ini hanya menanyakan
   * kabarnya. Berhenti setelah batas percobaan supaya penarikan yang macet
   * tidak membuat halaman menanyakannya selamanya.
   */
  async function pantau(id: string): Promise<void> {
    for (let i = 0; i < 60; i += 1) {
      await new Promise((r) => setTimeout(r, 1500))
      const job = await api.getJob(id)
      if (job.status !== 'BERJALAN') break
    }
    await muat(page)
  }

  if (dibuka !== null) {
    return <StationDetail sourceCode={dibuka} onBack={() => setDibuka(null)} />
  }

  const halamanTerakhir = Math.max(1, Math.ceil(total / 25))

  return (
    <div>
      <h1>Katalog Station</h1>
      <p className="hint">
        Ditarik dari Bank Soal OSCE dan hanya dibaca. Editor station dan editor rubrik bukan milik
        platform ini — bila ada yang keliru, perbaikannya di Bank Soal lalu tarik ulang.
      </p>

      {error !== null && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {health !== null && <Konektor health={health} />}

      <div className="page-actions">
        {bolehMenarik && (
          <button onClick={() => void tarik()} disabled={busy}>
            {busy ? 'Menarik…' : 'Tarik dari Bank Soal'}
          </button>
        )}
        <span className="hint">
          Menarik dua kali tidak menggandakan apa pun; yang isinya tidak berubah dilewati.
        </span>
      </div>

      {stations.length === 0 ? (
        <p className="dt-empty">Katalog masih kosong. Tarik dari Bank Soal untuk mengisinya.</p>
      ) : (
        <div className="dt">
          <table>
            <thead>
              <tr>
                <th>Kode</th>
                <th>Judul</th>
                <th>Kategori</th>
                <th>SKDI</th>
                <th>Durasi</th>
                <th>Versi</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {stations.map((s) => (
                <tr key={s.id}>
                  <td>
                    <code>{s.sourceCode}</code>
                  </td>
                  <td>{s.title}</td>
                  <td>{s.categoryName}</td>
                  <td>
                    {s.skdiCode ?? '—'}
                    {s.skdiLevel === null ? '' : ` · ${s.skdiLevel}`}
                  </td>
                  <td>{s.suggestedDurationMin === null ? '—' : `${s.suggestedDurationMin} mnt`}</td>
                  <td>v{s.version}</td>
                  <td>
                    <button onClick={() => setDibuka(s.sourceCode)}>Buka</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="dt-pager">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              ‹ Sebelumnya
            </button>
            <span>
              Halaman {page} dari {halamanTerakhir} — {total} station
            </span>
            <button disabled={page >= halamanTerakhir} onClick={() => setPage((p) => p + 1)}>
              Berikutnya ›
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function Konektor({ health }: { health: api.ConnectorHealth }): ReactNode {
  const job = health.penarikanTerakhir
  const selisih =
    job === null || job.reportedTotal === null ? null : job.reportedTotal - job.examined

  return (
    <section className="terbit-form">
      <div className="version-head">
        <h2>Konektor Bank Soal</h2>
        <span className={`badge ${BREAKER_CLASS[health.breaker.state]}`}>
          {health.breaker.state.replace(/_/g, ' ')}
        </span>
      </div>

      <div className="ringkas-kartu">
        <div>
          <strong>{health.jumlahStation}</strong>
          <span>station di katalog</span>
        </div>
        <div>
          <strong>{health.permintaan}</strong>
          <span>permintaan</span>
        </div>
        <div>
          <strong>{health.pengulangan}</strong>
          <span>pengulangan</span>
        </div>
        <div>
          <strong>{health.kredensial}</strong>
          <span>kredensial</span>
        </div>
      </div>

      {health.breaker.state === 'TERBUKA' && (
        <p className="notice">
          Konektor berhenti menghubungi Bank Soal setelah {health.breaker.consecutiveFailures}{' '}
          kegagalan beruntun. Ia mencoba lagi sendiri setelah jendela tunggu berlalu.
        </p>
      )}

      {job === null ? (
        <p className="hint">Belum pernah ada penarikan.</p>
      ) : (
        <>
          <p className="hint">
            Penarikan terakhir{' '}
            <span className={`badge ${JOB_CLASS[job.status]}`}>{job.status}</span> oleh{' '}
            {job.startedBy} · {waktu(job.startedAt)}
          </p>
          <ul className="ringkas-list">
            <li>
              <span>Diperiksa</span>
              <span>{job.examined}</span>
            </li>
            <li>
              <span>Versi baru dibuat</span>
              <span>{job.created}</span>
            </li>
            <li>
              <span>Tidak berubah</span>
              <span>{job.unchanged}</span>
            </li>
            <li>
              <span>Ditolak</span>
              <span>{job.rejected}</span>
            </li>
            <li>
              <span>
                Media tercermin
                <br />
                <span className="hint">
                  Disalin ke penyimpanan sendiri; tautan sumber tidak dipakai saat ujian.
                </span>
              </span>
              <span>{job.mediaMirrored}</span>
            </li>
            {job.reportedTotal !== null && (
              <li>
                <span>
                  Rekonsiliasi terhadap manifest Bank Soal
                  <br />
                  <span className="hint">
                    Tanpa angka pembanding, &ldquo;berhasil&rdquo; hanya berarti tidak ada galat.
                  </span>
                </span>
                <span>
                  {selisih === 0 ? (
                    <span className="badge badge-diterbitkan">SEIMBANG</span>
                  ) : (
                    <span className="badge badge-ditolak">KURANG {selisih}</span>
                  )}
                </span>
              </li>
            )}
          </ul>

          {job.lastError !== null && (
            <p role="alert" className="error">
              {job.lastError}
            </p>
          )}

          {job.categoryReport !== null && job.categoryReport !== undefined && (
            <details open={!job.categoryReport.balanced}>
              <summary>
                Rekonsiliasi per kategori ({job.categoryReport.categories} kategori){' '}
                {job.categoryReport.balanced ? (
                  <span className="badge badge-diterbitkan">SEIMBANG</span>
                ) : (
                  <span className="badge badge-ditolak">
                    {job.categoryReport.discrepancies.length} tidak cocok
                  </span>
                )}
              </summary>
              <p className="hint">
                Selisih yang saling menutup tidak terlihat pada total. Blueprint disusun per
                kategori, dan kategori yang <strong>berlebih</strong> biasanya berarti ada station
                yang sudah dicabut publikasinya di Bank Soal tetapi masih ada di katalog.
              </p>
              <ul className="ringkas-list">
                {job.categoryReport.discrepancies.map((d) => (
                  <li key={d.category}>
                    <span>
                      <strong>{d.category}</strong>
                      <br />
                      <span className="hint">{d.reason}</span>
                    </span>
                    <span>
                      <span
                        className={`badge ${d.kind === 'KURANG' ? 'badge-ditolak' : 'badge-diajukan'}`}
                      >
                        {d.kind} {d.delta}
                      </span>
                      <br />
                      <span className="hint">
                        manifest {d.reported} · katalog {d.local}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {(job.findings?.length ?? 0) > 0 && (
            <details>
              <summary>{job.findings?.length ?? 0} temuan pada penarikan terakhir</summary>
              <ul className="ringkas-list">
                {(job.findings ?? []).slice(0, 20).map((f, i) => (
                  <li key={i}>
                    <span>
                      <code>{f.caseCode}</code> {f.reason}
                      {f.detail === undefined ? null : (
                        <>
                          <br />
                          <span className="hint">
                            {f.detail
                              .slice(0, 3)
                              .map((d) => `${d.field}: ${d.message}`)
                              .join('; ')}
                          </span>
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  )
}

function StationDetail({
  sourceCode,
  onBack,
}: {
  sourceCode: string
  onBack: () => void
}): ReactNode {
  const [versions, setVersions] = useState<readonly api.StationVersion[] | null>(null)
  const [aktif, setAktif] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const dimuat = useRef(false)

  useEffect(() => {
    if (dimuat.current) return
    dimuat.current = true
    api
      .stationVersions(sourceCode)
      .then(setVersions)
      .catch((err: unknown) => setError(pesan(err)))
  }, [sourceCode])

  if (error !== null) {
    return (
      <div>
        <button onClick={onBack}>← Kembali</button>
        <p role="alert" className="error">
          {error}
        </p>
      </div>
    )
  }

  if (versions === null || versions.length === 0) {
    return (
      <div>
        <button onClick={onBack}>← Kembali</button>
        <p className="hint">Memuat…</p>
      </div>
    )
  }

  const v = versions[aktif] as api.StationVersion
  const totalBobot = v.rubricItems.reduce((n, r) => n + r.weight, 0)

  return (
    <div>
      <button onClick={onBack}>← Kembali</button>
      <h1>{v.title}</h1>
      <p className="hint">
        <code>{v.sourceCode}</code> · {v.categoryName}
        {v.skdiCode === null ? '' : ` · SKDI ${v.skdiCode} ${v.skdiLevel ?? ''}`}
        {v.suggestedDurationMin === null ? '' : ` · ${v.suggestedDurationMin} menit`}
      </p>

      {versions.length > 1 && (
        <div className="tabs">
          {versions.map((ver, i) => (
            <button
              key={ver.id}
              className={i === aktif ? 'aktif' : undefined}
              onClick={() => setAktif(i)}
            >
              v{ver.version}
            </button>
          ))}
        </div>
      )}

      <p className="hint">
        Ditarik {waktu(v.pulledAt)} · sidik isi <code>{v.contentHash.slice(0, 16)}…</code>
      </p>

      <h2>Rubrik</h2>
      <p className="hint">
        Delapan kompetensi, skor 0–3 berdeskripsi. Struktur ini milik Bank Soal dan tidak dapat
        diubah di sini. Total bobot {totalBobot}.
      </p>
      <div className="dt">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Kompetensi</th>
              <th>Bobot</th>
              <th>Deskripsi skor</th>
            </tr>
          </thead>
          <tbody>
            {v.rubricItems.map((r) => (
              <tr key={r.competencyOrder}>
                <td>{r.competencyOrder}</td>
                <td>{r.competencyName}</td>
                <td>{r.weight}</td>
                <td>
                  {/* Tanpa penomoran daftar: angka levelnya sudah ditulis
                      eksplisit, dan penomoran ol menggandakannya menjadi
                      "1. 0", "2. 1", yang justru menyesatkan pembaca. */}
                  <ul className="skor-list tanpa-nomor">
                    {[
                      r.score0Description,
                      r.score1Description,
                      r.score2Description,
                      r.score3Description,
                    ].map((teks, level) => (
                      <li key={level}>
                        <strong>{level}</strong> {teks ?? <span className="hint">kosong</span>}
                      </li>
                    ))}
                  </ul>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {v.globalRatingCriteria !== null && (
        <>
          <h2>Global Rating</h2>
          <ul className="ringkas-list">
            {Object.entries(v.globalRatingCriteria).map(([k, teks]) => (
              <li key={k}>
                <span>
                  <strong>{k}</strong> — {teks}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="naskah-layout">
        <section>
          <h2>Kebutuhan station</h2>
          <ul className="ringkas-list">
            <li>
              <span>Tata letak</span>
              <span>{v.requirement?.layout ?? '—'}</span>
            </li>
            <li>
              <span>Laboran</span>
              <span>{v.requirement?.staff ?? '—'}</span>
            </li>
            <li>
              <span>Manekin</span>
              <span>{v.requirement?.manikin ?? '—'}</span>
            </li>
            <li>
              <span>Alat</span>
              <span>{v.requirement?.tools ?? '—'}</span>
            </li>
          </ul>
        </section>

        <section>
          <h2>Media</h2>
          {v.media.length === 0 ? (
            <p className="dt-empty">Tidak ada.</p>
          ) : (
            <ul className="ringkas-list">
              {v.media.map((m) => (
                <li key={m.objectKey}>
                  <span>
                    <code>{m.objectKey}</code>
                    <br />
                    <span className="hint">
                      {m.mimeType} · {Math.ceil(m.sizeBytes / 1024)} KB · sidik{' '}
                      {m.contentHash.slice(0, 12)}…
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}

          <h2>Pertanyaan viva</h2>
          {v.vivaQuestions.length === 0 ? (
            <p className="dt-empty">Tidak ada.</p>
          ) : (
            <ol className="skor-list">
              {v.vivaQuestions.map((q) => (
                <li key={q.order}>{q.questionText}</li>
              ))}
            </ol>
          )}

          <h2>Atribusi</h2>
          <p className="hint">
            {v.attribution?.authorName ?? '—'}
            {v.attribution?.authorInstitution === undefined
              ? ''
              : ` · ${v.attribution.authorInstitution}`}
          </p>
        </section>
      </div>
    </div>
  )
}
